import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, LogIn, Hash } from 'lucide-react';
import { useGameStore } from '@/store';
import { isValidRoomCode } from '@party-game/shared';

// API URL - use port 8787 in development, Cloudflare Workers in production
const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_URL = isDev 
  ? 'http://localhost:8787' 
  : (import.meta.env.VITE_API_URL || 'https://party-game-api.ibrahem-magdy025.workers.dev');

export function JoinRoomPage() {
  const navigate = useNavigate();
  const { code: urlCode } = useParams();
  const { setPlayer, setRoom, setIsHost, setPlayers } = useGameStore();

  const [playerName, setPlayerName] = useState('');
  const [roomCode, setRoomCode] = useState(urlCode?.toUpperCase() || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-fill code from URL
  useEffect(() => {
    if (urlCode) {
      setRoomCode(urlCode.toUpperCase());
    }
  }, [urlCode]);

  const handleJoin = async () => {
    if (!playerName.trim()) {
      setError('الرجاء إدخال اسمك');
      return;
    }

    const code = roomCode.toUpperCase().trim();
    if (!isValidRoomCode(code)) {
      setError('كود الغرفة يجب أن يكون 6 أحرف');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/rooms/${code}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerName: playerName.trim(),
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'فشل في الانضمام');
      }

      // Store player and room info
      const newPlayer = {
        id: data.data.player.id,
        roomId: data.data.room.id,
        name: playerName.trim(),
        score: 0,
        role: 'player' as const,
        isConnected: true,
        joinedAt: new Date().toISOString(),
      };
      
      setPlayer(newPlayer);
      setPlayers([newPlayer]); // Initialize with current player, WebSocket will sync others

      const roomStatus = data.data.room.status || 'waiting';
      
      setRoom({
        id: data.data.room.id,
        code: data.data.room.code,
        hostId: '',
        gameType: data.data.room.gameType,
        status: roomStatus,
        currentRound: 0,
        maxPlayers: 10,
        createdAt: new Date().toISOString(),
      });

      setIsHost(false);

      // Navigate based on room status
      if (roomStatus === 'playing') {
        navigate(`/game/${code}`);
      } else {
        navigate(`/lobby/${code}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'حدث خطأ غير متوقع');
    } finally {
      setIsLoading(false);
    }
  };

  const formatRoomCode = (value: string) => {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 text-white/60 hover:text-white transition-colors"
        >
          <ArrowRight className="w-5 h-5" />
          العودة للرئيسية
        </button>

        <div className="card">
          <h1 className="text-3xl font-bold mb-2 text-center">انضم للعبة</h1>
          <p className="text-white/60 text-center mb-8">أدخل كود الغرفة للانضمام</p>

          {/* Room code */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2 flex items-center gap-2">
              <Hash className="w-4 h-4" />
              كود الغرفة
            </label>
            <input
              type="text"
              value={roomCode}
              onChange={(e) => setRoomCode(formatRoomCode(e.target.value))}
              placeholder="مثال: ABC123"
              className="input text-center text-2xl tracking-widest font-mono"
              maxLength={6}
              style={{ direction: 'ltr' }}
            />
          </div>

          {/* Player name */}
          <div className="mb-8">
            <label className="block text-sm font-medium mb-2">اسمك</label>
            <input
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="أدخل اسمك..."
              className="input"
              maxLength={20}
            />
          </div>

          {/* Error message */}
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-game-accent text-center mb-4"
            >
              {error}
            </motion.p>
          )}

          {/* Join button */}
          <button
            onClick={handleJoin}
            disabled={isLoading || roomCode.length !== 6}
            className="btn-secondary w-full flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                جاري الانضمام...
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                انضم الآن
              </>
            )}
          </button>

          {/* Visual code indicator */}
          <div className="mt-6 flex justify-center gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl font-bold transition-all ${
                  roomCode[i]
                    ? 'bg-primary-500/30 border-2 border-primary-500'
                    : 'bg-white/10 border-2 border-white/20'
                }`}
              >
                {roomCode[i] || ''}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
